import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect, useMemo } from 'react';
import { PeerTubePlayer } from '@peertube/embed-api';
import './VideoPlayer.css';
import SimpleMarkdown from './SimpleMarkdown';

const VideoPlayer = forwardRef(
  ({ url, onSeek, onProgress, onEnded, shareSlug, password, accessToken, webinarTitle, webinarDate, speakerName, chronologyStructure }, ref) => {
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const videoRef = useRef(null);
  const userMutedRef = useRef(false);
  const audioContextRef = useRef(null);
  const endedNotifiedRef = useRef(false);
  const onEndedRef = useRef(onEnded);
  const onProgressRef = useRef(onProgress);
  const iframeRef = useRef(null);
  const peerTubeDurationRef = useRef(0);
  const [isChronologyOpen, setIsChronologyOpen] = useState(false);
  const [peerTubeSrc, setPeerTubeSrc] = useState('');

  let videoUrl = url;
  if (url && !url.startsWith('http')) {
    const baseUrl = process.env.REACT_APP_API_URL;
    videoUrl = baseUrl ? `${baseUrl}${url}` : url;
  }
  if (videoUrl && shareSlug) {
    const separator = videoUrl.includes('?') ? '&' : '?';
    videoUrl = `${videoUrl}${separator}share_slug=${encodeURIComponent(shareSlug)}`;
    if (password) {
      videoUrl += `&password=${encodeURIComponent(password)}`;
    }
    if (accessToken) {
      videoUrl += `&access=${encodeURIComponent(accessToken)}`;
    }
  }
  const applyPeerTubeEmbedParams = (urlValue, startSeconds = null) => {
    try {
      const parsed = new URL(urlValue);
      // Отключаем P2P-режим в плеере, чтобы не показывалось предупреждение про IP.
      parsed.searchParams.set('p2p', '0');
      // Скрываем верхний заголовок/предупреждение в embed-плеере.
      parsed.searchParams.set('warningTitle', '0');
      parsed.searchParams.set('title', '0');
      // Скрываем кнопку/ссылку PeerTube на страницу инстанса.
      parsed.searchParams.set('peertubeLink', '0');
      // Включаем JS API, иначе родительская страница не узнаёт о конце ролика.
      parsed.searchParams.set('api', '1');
      // Оставляем стандартные контролы плеера.
      parsed.searchParams.set('controls', '1');
      if (typeof startSeconds === 'number') {
        parsed.searchParams.set('start', String(Math.max(0, Math.floor(startSeconds))));
      }
      return parsed.toString();
    } catch {
      return urlValue;
    }
  };

  const peerTubeEmbedUrl = useMemo(() => {
    if (!videoUrl) return null;
    const rawUrl = String(videoUrl).trim();
    if (!rawUrl) return null;

    try {
      const parsed = new URL(rawUrl);
      const path = parsed.pathname || '';
      const host = (parsed.hostname || '').toLowerCase();
      const isLikelyPeerTube =
        host.includes('peertube') ||
        host === 'toobe.novacutan.ru' ||
        path.startsWith('/w/') ||
        path.startsWith('/videos/watch/') ||
        path.startsWith('/videos/embed/');

      if (!isLikelyPeerTube) return null;

      if (path.startsWith('/videos/embed/')) {
        return applyPeerTubeEmbedParams(parsed.toString());
      }

      let videoId = '';
      if (path.startsWith('/w/')) {
        videoId = path.split('/w/')[1]?.split('/')[0] || '';
      } else if (path.startsWith('/videos/watch/')) {
        videoId = path.split('/videos/watch/')[1]?.split('/')[0] || '';
      }
      if (!videoId) {
        videoId = parsed.searchParams.get('v') || '';
      }
      if (!videoId) return null;

      return applyPeerTubeEmbedParams(`${parsed.origin}/videos/embed/${videoId}`);
    } catch {
      return null;
    }
  }, [videoUrl]);
  const isPeerTubeSource = !!peerTubeEmbedUrl;

  const buildPeerTubeStartUrl = (baseUrl, seconds) => {
    return applyPeerTubeEmbedParams(baseUrl, seconds);
  };

  useEffect(() => {
    if (!isPeerTubeSource) {
      setPeerTubeSrc('');
      return;
    }
    setPeerTubeSrc(peerTubeEmbedUrl || '');
  }, [isPeerTubeSource, peerTubeEmbedUrl]);

  useEffect(() => {
    if (isPeerTubeSource) {
      setStarted(true);
      setLoading(false);
    } else {
      setStarted(false);
    }
  }, [isPeerTubeSource, videoUrl]);

  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);

  useEffect(() => {
    endedNotifiedRef.current = false;
    peerTubeDurationRef.current = 0;
  }, [videoUrl]);

  const notifyEnded = () => {
    if (endedNotifiedRef.current) return;
    endedNotifiedRef.current = true;
    if (typeof onEndedRef.current === 'function') {
      onEndedRef.current();
    }
  };

  const maybeNotifyFromProgress = (currentTime, duration) => {
    const position = Number(currentTime);
    const total = Number(duration);
    if (!Number.isFinite(position) || !Number.isFinite(total) || total < 1) return;
    const remaining = total - position;
    if (remaining <= 5 || position / total >= 0.995) {
      notifyEnded();
    }
  };

  const isPeerTubeEndedPayload = (raw) => {
    let data = raw;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return false;
      }
    }
    if (data == null) return false;
    if (data === 'ended') return true;
    if (typeof data !== 'object') return false;
    const method = String(data.method || data.type || data.event || '');
    const nested = data.params || data.data || data;
    const state = String(
      (nested && nested.playbackState) || nested.status || nested.state || nested || ''
    ).toLowerCase();
    if (state === 'ended' || method.toLowerCase().includes('ended')) {
      return true;
    }
    if (nested && typeof nested === 'object') {
      if (typeof nested.position === 'number' || typeof nested.duration === 'number') {
        if (Number(nested.duration) > 0) {
          peerTubeDurationRef.current = Number(nested.duration);
        }
        maybeNotifyFromProgress(nested.position, nested.duration || peerTubeDurationRef.current);
      }
    }
    return false;
  };

  useEffect(() => {
    if (!isPeerTubeSource || typeof onEnded !== 'function') return undefined;
    const handler = (event) => {
      if (isPeerTubeEndedPayload(event.data)) {
        notifyEnded();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isPeerTubeSource, onEnded, videoUrl]);

  useEffect(() => {
    if (!isPeerTubeSource || typeof onEnded !== 'function') return undefined;
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    let cancelled = false;
    let player = null;
    let pollId = null;
    let bound = false;

    const handleStatus = (data) => {
      const payload = data && typeof data === 'object' ? data : {};
      const position = Number(payload.position);
      const duration = Number(payload.duration);
      const state = String(payload.playbackState || data || '').toLowerCase();
      if (Number.isFinite(duration) && duration > 0) {
        peerTubeDurationRef.current = duration;
      }
      if (typeof onProgressRef.current === 'function' && Number.isFinite(position)) {
        onProgressRef.current(position);
      }
      if (state === 'ended') {
        notifyEnded();
        return;
      }
      maybeNotifyFromProgress(position, duration || peerTubeDurationRef.current);
    };

    const setupPlayer = async () => {
      if (cancelled || bound || !iframeRef.current) return;
      bound = true;
      try {
        player = new PeerTubePlayer(iframeRef.current);
        await player.ready;
        if (cancelled) return;
        player.addEventListener('playbackStatusUpdate', handleStatus);
        player.addEventListener('playbackStatusChange', (state) => {
          if (String(state || '').toLowerCase() === 'ended') {
            notifyEnded();
          }
        });
        pollId = window.setInterval(async () => {
          if (cancelled || !player) return;
          try {
            const position = await player.getCurrentTime();
            maybeNotifyFromProgress(position, peerTubeDurationRef.current);
          } catch {
            // PeerTube ещё не готов или метод недоступен.
          }
        }, 1000);
      } catch (error) {
        bound = false;
        console.warn('PeerTube embed API недоступен:', error);
      }
    };

    const onLoad = () => {
      setupPlayer();
    };
    iframe.addEventListener('load', onLoad);
    const timeoutId = window.setTimeout(setupPlayer, 1200);

    return () => {
      cancelled = true;
      iframe.removeEventListener('load', onLoad);
      window.clearTimeout(timeoutId);
      if (pollId) window.clearInterval(pollId);
    };
  }, [isPeerTubeSource, peerTubeSrc, onEnded]);

  // Инициализация AudioContext при загрузке компонента
  useEffect(() => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        audioContextRef.current = new AudioContextClass();
        console.log('AudioContext created, state:', audioContextRef.current.state);
      }
    } catch (e) {
      console.log('Web Audio not supported:', e);
    }
    
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Функция активации AudioContext
  const activateAudioContext = async () => {
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
        console.log('AudioContext activated successfully');
        
        // Создаем и сразу останавливаем тихий буфер для полной активации
        const buffer = audioContextRef.current.createBuffer(1, 1, 22050);
        const source = audioContextRef.current.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContextRef.current.destination);
        source.start();
        source.stop(0.001);
        
        return true;
      } catch (e) {
        console.warn('Failed to activate AudioContext:', e);
      }
    }
    return false;
  };

  // Функция принудительного включения звука
  const forceEnableAudio = (video, force = false) => {
    if (!video) return false;
    
    // Если пользователь сам выключил звук, не включаем
    if (userMutedRef.current && !force) {
      console.log('User muted, respecting choice');
      return false;
    }

    try {
      // Прямое управление нативным элементом
      video.muted = false;
      video.volume = 1.0;
      
      // Дополнительно пробуем через свойство (для старых браузеров)
      if (video.hasAttribute('muted')) {
        video.removeAttribute('muted');
      }
      
      // Проверяем, что звук действительно включился
      const success = !video.muted && video.volume > 0;
      
      if (success) {
        console.log('Audio enabled successfully');
        setAudioEnabled(true);
      }
      
      return success;
    } catch (e) {
      console.warn('Error enabling audio:', e);
      return false;
    }
  };

  // Следим за изменениями звука от пользователя
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleVolumeChange = () => {
      if (video.muted) {
        console.log('User muted video');
        userMutedRef.current = true;
        setAudioEnabled(false);
      } else {
        console.log('User unmuted video');
        userMutedRef.current = false;
        setAudioEnabled(true);
      }
    };

    video.addEventListener('volumechange', handleVolumeChange);
    return () => video.removeEventListener('volumechange', handleVolumeChange);
  }, []);

  const formattedWebinarDate = useMemo(() => {
    if (!webinarDate) return '';
    try {
      const d = new Date(webinarDate);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
      return '';
    }
  }, [webinarDate]);

  const parseHmsToSeconds = (hms) => {
    const parts = String(hms).split(':').map((v) => parseInt(v, 10));
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  };

  const chronologyEntries = useMemo(() => {
    if (!chronologyStructure || !String(chronologyStructure).trim()) return [];
    return String(chronologyStructure)
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const match = line.match(/\b(\d{2}:\d{2}:\d{2})\b/);
        const displayText = match ? line.replace(match[1], '').replace(/\s{2,}/g, ' ').trim() : line;
        return {
          line,
          displayText,
          timeLabel: match ? match[1] : null,
          seconds: match ? parseHmsToSeconds(match[1]) : null,
        };
      });
  }, [chronologyStructure]);

  const seekToChronologyPoint = async (seconds) => {
    if (isPeerTubeSource) {
      if (typeof seconds === 'number' && peerTubeEmbedUrl) {
        setPeerTubeSrc(buildPeerTubeStartUrl(peerTubeEmbedUrl, seconds));
      }
      setIsChronologyOpen(false);
      return;
    }
    const video = videoRef.current;
    if (!video || typeof seconds !== 'number') return;

    if (!started) {
      await handleStart();
    }
    setTimeout(() => {
      try {
        video.currentTime = seconds;
        if (onSeek) onSeek(seconds);
      } catch (error) {
        console.error('Error seeking from chronology:', error);
      }
    }, started ? 0 : 200);
    setIsChronologyOpen(false);
  };

  const handleManualUnmute = () => {
    const video = videoRef.current;
    if (!video) return;
    userMutedRef.current = false;
    forceEnableAudio(video, true);
  };

  useImperativeHandle(ref, () => ({
    seekTo: (seconds) => {
      if (isPeerTubeSource) return;
      try {
        const video = videoRef.current;
        if (video) {
          video.currentTime = seconds;
          if (onSeek) onSeek(seconds);
        }
      } catch (error) {
        console.error('Error seeking video:', error);
      }
    },
    getCurrentTime: () => {
      if (isPeerTubeSource) return 0;
      try {
        const video = videoRef.current;
        return video ? video.currentTime || 0 : 0;
      } catch {
        return 0;
      }
    },
  }));

  // УЛУЧШЕННАЯ ВЕРСИЯ HANDLESTART
  const handleStart = async () => {
    if (isPeerTubeSource) {
      setStarted(true);
      setLoading(false);
      return;
    }
    const video = videoRef.current;
    if (!video) {
      setStarted(true);
      setLoading(false);
      return;
    }

    setLoading(true);

    // 1. Сначала активируем AudioContext (для iOS это критично)
    await activateAudioContext();

    // 2. Запускаем видео БЕЗ ЗВУКА (требование iOS)
    video.muted = true;
    
    const playPromise = video.play();
    
    const enableAudio = () => {
      if (userMutedRef.current) {
        console.log('User muted, not enabling audio automatically');
        return;
      }

      // Многократные попытки с разными задержками
      const delays = [30, 50, 100, 200, 300, 500, 1000];
      
      delays.forEach((delay, index) => {
        setTimeout(() => {
          if (video && !userMutedRef.current) {
            const success = forceEnableAudio(video, index === delays.length - 1); // последняя попытка принудительная
            if (success) {
              console.log(`Audio enabled after ${delay}ms`);
            }
          }
        }, delay);
      });
    };

    const handlePlaySuccess = () => {
      setStarted(true);
      
      // Первая попытка через 30мс (очень быстро)
      setTimeout(() => {
        if (!userMutedRef.current && video) {
          forceEnableAudio(video);
        }
      }, 30);
      
      // Затем запускаем серию попыток
      enableAudio();
    };

    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(handlePlaySuccess)
        .catch(error => {
          console.warn('Play failed:', error);
          setStarted(true);
          setLoading(false);
          // Даже если play не удался, пробуем включить звук через 100мс
          setTimeout(() => forceEnableAudio(video, true), 100);
        });
    } else {
      handlePlaySuccess();
    }
  };

  return (
    <div className="video-player-root">
      <div className="video-player-container">
        {(!started || loading) && (
          <div className="video-start-overlay">
            {!started && !loading && (
              <div className="video-start-content">
                <div className="video-start-meta">
                  {webinarTitle && <div className="video-start-meta-title">{webinarTitle}</div>}
                  {formattedWebinarDate && (
                    <div className="video-start-meta-date">{formattedWebinarDate}</div>
                  )}
                  {speakerName && <div className="video-start-meta-speaker">{speakerName}</div>}
                </div>
                <button className="video-start-button" onClick={handleStart}>
                  ▶ Начать просмотр
                </button>
              </div>
            )}
            {loading && (
              <div className="video-loading">
                <div className="video-loading-spinner" />
                <div className="video-loading-text">Загружаем видео…</div>
              </div>
            )}
          </div>
        )}
        <div
          className="video-wrapper"
          onContextMenu={(e) => e.preventDefault()}
        >
          {(webinarTitle || formattedWebinarDate) && (
            <div className="video-corner-meta" aria-hidden="true">
              {webinarTitle && <div className="video-corner-meta-title">{webinarTitle}</div>}
              {formattedWebinarDate && (
                <div className="video-corner-meta-date">{formattedWebinarDate}</div>
              )}
            </div>
          )}
          {chronologyEntries.length > 0 && (
            <button
              type="button"
              className="video-chronology-toggle"
              onClick={() => setIsChronologyOpen(true)}
              title="Хронология"
            >
              <span className="video-chronology-icon">≡</span>
              <span>Хронология</span>
            </button>
          )}

          {isPeerTubeSource ? (
            <iframe
              key={peerTubeSrc || peerTubeEmbedUrl}
              ref={iframeRef}
              title={webinarTitle || 'PeerTube video'}
              src={peerTubeSrc || peerTubeEmbedUrl}
              allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              playsInline
              webkit-playsinline="true"
              x5-playsinline="true"
              preload="metadata"
              controlsList="nodownload"
              disablePictureInPicture
              disableRemotePlayback
              onCanPlay={() => setLoading(false)}
              onPlaying={() => setLoading(false)}
              onTimeUpdate={() => {
                if (videoRef.current) {
                  if (typeof onProgress === 'function') {
                    onProgress(videoRef.current.currentTime || 0);
                  }
                  maybeNotifyFromProgress(videoRef.current.currentTime || 0, videoRef.current.duration || 0);
                }
              }}
              onEnded={notifyEnded}
              onError={() => setLoading(false)}
            />
          )}
          {started && !isPeerTubeSource && !audioEnabled && !loading && (
            <button
              type="button"
              className="video-unmute-button"
              onClick={handleManualUnmute}
            >
              Включить звук
            </button>
          )}

          {isChronologyOpen && (
            <div className="video-chronology-overlay" onClick={() => setIsChronologyOpen(false)}>
              <div className="video-chronology-panel" onClick={(e) => e.stopPropagation()}>
                <div className="video-chronology-header">
                  <strong>Хронология и структура</strong>
                  <button
                    type="button"
                    className="video-chronology-close"
                    onClick={() => setIsChronologyOpen(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="video-chronology-list">
                  {chronologyEntries.map((entry, index) => (
                    entry.timeLabel && typeof entry.seconds === 'number' ? (
                      <button
                        key={`${entry.line}-${index}`}
                        type="button"
                        className="video-chronology-item video-chronology-item-clickable"
                        onClick={() => seekToChronologyPoint(entry.seconds)}
                        title="Перейти к фрагменту"
                      >
                        <span className="video-chronology-time">{entry.timeLabel}</span>
                        <SimpleMarkdown text={entry.displayText} className="video-chronology-text" />
                      </button>
                    ) : (
                      <div key={`${entry.line}-${index}`} className="video-chronology-item">
                        <SimpleMarkdown text={entry.displayText} className="video-chronology-text" />
                      </div>
                    )
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

VideoPlayer.displayName = 'VideoPlayer';
export default VideoPlayer;